import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Play,
  Lock,
  Layers,
  Sparkles,
  Download,
  Upload,
  KeyRound,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X
} from "lucide-react";
import { useWorkflows } from "../../hooks/useWorkflows";
import { canEditWorkflows } from "../../services/authService";
import {
  exportWorkflow,
  exportAllWorkflows
} from "../../services/workflowService";
import { downloadJson, slugify } from "../../utils/downloadUtils";
import { LoadingState, ErrorState, EmptyState } from "../ui/LoadingStates";
import WorkflowStatCard from "./WorkflowStatCard";
import WorkflowCard from "./WorkflowCard";
import RunVisualizer from "./RunVisualizer";
import AIBuilderDialog from "./AIBuilderDialog";
import CloneWorkflowDialog from "./CloneWorkflowDialog";
import ImportWorkflowDialog from "./ImportWorkflowDialog";

/** Total credentials the server stripped across a bundle. */
const countRedactions = (bundle) =>
  (bundle?.workflows || []).reduce(
    (n, w) => n + (w.redactions?.length || 0),
    0
  );

/**
 * Workflows home — lists the org's runnable workflows (real API data).
 * Provider-bundled workflows the org isn't connected to render gated.
 * Header is provided by TopBar (pageHeaders.workflows); this is content-only.
 */
export default function WorkflowsHome() {
  const navigate = useNavigate();
  const { workflows, loading, error, refresh } = useWorkflows();
  const [runTarget, setRunTarget] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [notice, setNotice] = useState(null); // {tone, message, detail?}
  const canBuild = canEditWorkflows();

  const runnable = workflows.filter((w) => w.is_accessible !== false).length;
  const locked = workflows.filter((w) => w.is_accessible === false).length;
  // Only workflows this org owns can leave it; provider/global tiers are
  // platform assets and the server refuses to export them from a tenant.
  const exportable = workflows.filter(
    (w) => w.visibility === "org" || w.visibility === "personal"
  ).length;

  const stats = [
    {
      title: "Workflows",
      value: workflows.length,
      icon: Layers,
      gradient: "bg-gradient-to-br from-blue-50 to-indigo-50",
      iconBg: "bg-blue-100"
    },
    {
      title: "Ready to run",
      value: runnable,
      icon: Play,
      gradient: "bg-gradient-to-br from-green-50 to-emerald-50",
      iconBg: "bg-green-100"
    },
    {
      title: "Available with a provider",
      value: locked,
      icon: Lock,
      gradient: "bg-gradient-to-br from-amber-50 to-orange-50",
      iconBg: "bg-amber-100"
    }
  ];

  const announceExport = (bundle, label) => {
    const stripped = countRedactions(bundle);
    setNotice({
      tone: "success",
      message: `Exported ${label}.`,
      detail: stripped
        ? `${stripped} credential${stripped > 1 ? "s were" : " was"} stripped from the file — whoever imports it will need to re-enter ${stripped > 1 ? "them" : "it"}.`
        : "No credentials were found in these definitions, so nothing had to be stripped."
    });
  };

  const handleExportOne = async (workflow) => {
    setNotice(null);
    setExportingId(workflow.id);
    const res = await exportWorkflow(workflow.id);
    setExportingId(null);
    if (!res.success) {
      setNotice({ tone: "error", message: res.message });
      return;
    }
    downloadJson(`workflow-${slugify(workflow.name, "export")}`, res.data);
    announceExport(res.data, `“${workflow.name}”`);
  };

  const handleExportAll = async () => {
    setNotice(null);
    setExportingId("all");
    const res = await exportAllWorkflows();
    setExportingId(null);
    if (!res.success) {
      setNotice({ tone: "error", message: res.message });
      return;
    }
    const count = res.data?.workflows?.length || 0;
    if (!count) {
      setNotice({
        tone: "error",
        message: "This organization doesn't own any workflows to export yet."
      });
      return;
    }
    downloadJson("orbit-workflows", res.data);
    announceExport(res.data, `${count} workflow${count > 1 ? "s" : ""}`);
  };

  const handleImported = (created) => {
    const n = created?.length || 0;
    setNotice({
      tone: "success",
      message: `Imported ${n} workflow${n === 1 ? "" : "s"}.`,
      detail:
        "Any credentials stripped on export must be re-entered before these can run."
    });
    refresh();
  };

  const handleCloned = (created) => {
    setNotice({ tone: "success", message: `Created “${created.name}”.` });
    refresh();
  };

  return (
    <div className="flex-1 overflow-auto bg-[#FCFCFC]">
      <div className="max-w-[1400px] mx-auto p-8">
        {!loading && !error && (
          <div className="flex flex-wrap justify-end items-center gap-2 mb-6">
            <button
              onClick={handleExportAll}
              disabled={exportingId !== null || exportable === 0}
              title={
                exportable === 0
                  ? "This organization doesn't own any workflows yet"
                  : `Download all ${exportable} workflow${exportable > 1 ? "s" : ""} this organization owns`
              }
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {exportingId === "all" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export all
            </button>
            {canBuild && (
              <button
                onClick={() => setImportOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Upload className="w-4 h-4" /> Import
              </button>
            )}
            {canBuild && (
              <button
                onClick={() => setAiOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 hover:from-violet-600 hover:to-blue-600 shadow-sm"
              >
                <Sparkles className="w-4 h-4" /> Build with AI
              </button>
            )}
          </div>
        )}

        {notice && (
          <div
            className={`flex items-start gap-2 p-3 mb-6 rounded-lg border text-sm ${
              notice.tone === "error"
                ? "bg-red-50 border-red-200 text-red-700"
                : "bg-green-50 border-green-200 text-green-800"
            }`}
          >
            {notice.tone === "error" ? (
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium">{notice.message}</p>
              {notice.detail && (
                <p className="flex items-start gap-1.5 mt-1 text-xs opacity-90">
                  <KeyRound className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  {notice.detail}
                </p>
              )}
            </div>
            <button
              onClick={() => setNotice(null)}
              className="p-0.5 rounded hover:bg-black/5 shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading ? (
          <LoadingState message="Loading workflows…" size="large" />
        ) : error ? (
          <ErrorState message={error} />
        ) : workflows.length === 0 ? (
          <EmptyState
            icon={Layers}
            message="No workflows available for your organization yet."
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {stats.map((stat, index) => (
                <WorkflowStatCard key={index} {...stat} />
              ))}
            </div>

            <div className="space-y-4">
              {workflows.map((workflow) => (
                <WorkflowCard
                  key={workflow.id}
                  workflow={workflow}
                  busy={exportingId === workflow.id}
                  onRun={(wf) => setRunTarget(wf)}
                  onConnect={() => navigate("/dashboard")}
                  onOpen={(wf) => navigate(`/workflows/${wf.id}`)}
                  onClone={(wf) => setCloneTarget(wf)}
                  onExport={handleExportOne}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {runTarget && (
        <RunVisualizer
          workflow={runTarget}
          onClose={() => setRunTarget(null)}
        />
      )}

      <CloneWorkflowDialog
        workflow={cloneTarget}
        onClose={() => setCloneTarget(null)}
        onCloned={handleCloned}
      />

      <ImportWorkflowDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={handleImported}
      />

      <AIBuilderDialog open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
